import * as React from "react";
import {
  Loader2,
  Upload,
  X,
  CircleIcon,
  SquareIcon,
  Paintbrush,
  Trash2,
  Download,
  Plus,
  Palette,
  Hash,
  ZoomIn,
  ZoomOut,
  Move,
  Layout,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Menubar } from "@/components/ui/menubar";
import { useDropzone } from "react-dropzone";
import { Canvas } from "fabric";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toggle } from "@/components/ui/toggle";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Configuration } from "@/api/get-configuration";
import { useMachine } from "@xstate/react";
import { imageEditorMachine } from "./state";

interface ImageEditorProps {
  onSave: (canvases: Canvas[]) => Promise<void>;
  configuration?: Configuration | null;
}

const ImageEditor: React.FC<ImageEditorProps> = ({ onSave, configuration }) => {
  const [state, send] = useMachine(imageEditorMachine, {
    input: { configuration, onSave } as { configuration: Configuration; onSave: (canvases: Canvas[]) => Promise<void> },
  });

  const { 
    canvasApis, 
    current, 
    circleCounter, 
    squareCounter, 
    selectedColor, 
    showNumbers, 
    shapeSize, 
    zoomLevel
  } = state.context;

  const isLoading = state.matches("loading");
  const isExporting = state.matches("exporting");
  const circleMode = state.matches("circleMode");
  const squareMode = state.matches("squareMode");
  const paintMode = state.matches("paintMode");
  const isPanning = state.matches("panMode");

  // Handle keyboard events - this can be moved to a higher level component or kept minimal
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        send({ type: "ESCAPE_ALL_MODES" });
      }
      if (event.key === "Enter") {
        send({ type: "EXPORT_PDF" });
      }
      if (event.key === "Backspace") {
        const activeObjects = canvasApis[current]?.getActiveObjects();
        if (canvasApis[current] && activeObjects) {
          activeObjects.forEach((object) => {
            canvasApis[current].remove(object);
          });
          canvasApis[current].discardActiveObject();
          canvasApis[current].renderAll();
        }
      }
      if (event.key === "r" || event.key === "R") {
        send({ type: "RESET_ZOOM" });
      }
    };
    
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [send, canvasApis, current]);

  const addCanvases = (files: File[]) => {
    send({ type: "LOAD_FILES", files });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "image/png": [".png"],  
      "image/jpeg": [".jpg", ".jpeg"],
    },
    multiple: true,
    onDrop: (acceptedFiles) => addCanvases(acceptedFiles),
  });

  return (
    <div className="grid">
      {canvasApis.length === 0 && (
        <div className="grid gap-4">
          <div
            {...getRootProps()}
            className="w-full h-[250px] border-2 border-dashed rounded-md bg-accent/10 border-accent cursor-pointer"
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center h-full text-center w-full">
              {isLoading ? (
                <>
                  <div className="flex items-center gap-2 mb-6">
                    <Loader2 className="size-10 text-primary/60 animate-spin" />
                  </div>
                  <p className="text-sm font-medium text-foreground/80">
                    Se están procesando los archivos
                  </p>
                  <p className="text-xs text-muted-foreground/80">
                    Esto puede tardar unos segundos
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-6">
                    <Layout className="size-10 text-primary/60" />
                    <p className="text-4xl font-bold text-primary/60">Layout</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Upload className="size-4 text-foreground/80" />
                    <p className="text-sm font-medium text-foreground/80">
                      {isDragActive
                        ? "Suelte los archivos aquí"
                        : "Arrastre los archivos aquí o haga clic para seleccionar"}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground/80">
                    Imágenes (PNG, JPG, WEBP) o PDFs
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {canvasApis.length > 0 && (
        <div className="grid gap-0 rounded-md bg-[#2a2a2e]">
          <div className="flex flex-col w-full top-0 relative">
            <div className="flex justify-between w-full">
              <Menubar className="relative rounded-lg h-8 shadow-md justify-center w-fit flex gap-2 bg-sidebar top-1 left-1 border z-50">
                <Toggle
                  pressed={squareMode}
                  onPressedChange={() => {
                    send({ type: "TOGGLE_SQUARE_MODE" });
                  }}
                  size="sm"
                  className="size-6 hover:bg-sidebar-accent p-0 m-0 hover:text-sidebar-accent-foreground data-[state=on]:bg-muted/10 data-[state=on]:text-sidebar-accent-foreground"
                >
                  <SquareIcon className="size-4" />
                </Toggle>

                <Toggle
                  pressed={circleMode}
                  onPressedChange={() => {
                    send({ type: "TOGGLE_CIRCLE_MODE" });
                  }}
                  size="sm"
                  className="size-6 hover:bg-sidebar-accent p-0 m-0 hover:text-sidebar-accent-foreground data-[state=on]:bg-muted/10 data-[state=on]:text-sidebar-accent-foreground"
                >
                  <CircleIcon className="size-4" />
                </Toggle>

                <Toggle
                  pressed={paintMode}
                  onPressedChange={() => {
                    send({ type: "TOGGLE_PAINT_MODE" });
                  }}
                  size="sm"
                  className="size-6 hover:bg-sidebar-accent p-0 m-0 hover:text-sidebar-accent-foreground data-[state=on]:bg-muted/10"
                >
                  <Paintbrush
                    className="size-4"
                    style={{ color: selectedColor }}
                  />
                </Toggle>

                <Toggle
                  pressed={showNumbers}
                  onPressedChange={() => {
                    send({ type: "TOGGLE_NUMBERS" });
                  }}
                  size="sm"
                  className="size-6 hover:bg-sidebar-accent p-0 m-0 hover:text-sidebar-accent-foreground data-[state=on]:bg-muted/10 data-[state=on]:text-sidebar-accent-foreground"
                >
                  <Hash className="size-4" />
                </Toggle>

                <Label
                  htmlFor="color-input"
                  className="h-6 rounded-md flex items-center justify-center cursor-pointer hover:bg-sidebar-accent px-2"
                >
                  <Input
                    id="color-input"
                    name="color-input"
                    type="color"
                    value={selectedColor}
                    onChange={(e) => send({ type: "SET_COLOR", color: e.target.value })}
                    className="hidden"
                    title="Seleccionar color"
                  />
                  <Palette className="size-4" style={{ color: selectedColor }} />
                </Label>
              </Menubar>

              {/* Zoom controls in center */}
              <Menubar className="absolute left-1/2 transform -translate-x-1/2 rounded-lg h-8 shadow-md justify-center w-fit flex gap-2 bg-sidebar top-1 border z-50">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => send({ type: "ZOOM_OUT" })}
                  disabled={zoomLevel <= 0.5}
                  className="hover:bg-sidebar-accent px-1 h-6 w-6"
                  title="Zoom Out"
                >
                  <ZoomOut className="size-4" />
                </Button>
                
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => send({ type: "RESET_ZOOM" })}
                  className="hover:bg-sidebar-accent px-2 h-6 text-xs font-mono min-w-12"
                  title="Reset Zoom (R)"
                >
                  {Math.round(zoomLevel * 100)}%
                </Button>
                
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => send({ type: "ZOOM_IN" })}
                  disabled={zoomLevel >= 20}
                  className="hover:bg-sidebar-accent px-1 h-6 w-6"
                  title="Zoom In"
                >
                  <ZoomIn className="size-4" />
                </Button>

                <Separator orientation="vertical" className="mx-1" />

                <Toggle
                  pressed={isPanning}
                  onPressedChange={() => {
                    send({ type: "TOGGLE_PAN_MODE" });
                  }}
                  size="sm"
                  className="size-6 hover:bg-sidebar-accent p-0 m-0 hover:text-sidebar-accent-foreground data-[state=on]:bg-muted/10 data-[state=on]:text-sidebar-accent-foreground"
                  title="Pan Mode"
                >
                  <Move className="size-4" />
                </Toggle>
              </Menubar>

              <Menubar className="relative rounded-lg h-8 shadow-sm justify-center w-fit flex gap-2 bg-sidebar top-1 right-1 border z-50">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => send({ type: "EXPORT_PDF" })}
                  disabled={isExporting || canvasApis.length === 0}
                  className="hover:bg-sidebar-accent px-2 h-6"
                >
                  {isExporting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Download className="size-4" />
                  )}
                </Button>

                <Label
                  htmlFor="file-input"
                  className="rounded-md flex items-center justify-center gap-1 cursor-pointer text-xs hover:bg-sidebar-accent px-2 h-6"
                >
                  <Input
                    id="file-input"
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={async (e) => {
                      const files = (e.target as HTMLInputElement).files;
                      if (files) {
                        addCanvases(Array.from(files));
                      }
                    }}
                    className="hidden"
                  />
                  <Plus className="size-4" />
                </Label>
                <Separator orientation="vertical" className="mx-1" />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  onClick={() => send({ type: "REMOVE_ALL_IMAGES" })}
                  className="cursor-pointer size-6"
                >
                  <Trash2 className="size-4" />
                </Button>
              </Menubar>
            </div>

            {squareMode && (
              <Menubar className="absolute rounded-lg h-8 shadow-md justify-center w-fit flex gap-2 bg-sidebar top-10 left-1 z-50 border">
                <div className="flex items-center gap-2">
                  <Input 
                    id="square-counter"
                    value={squareCounter} 
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value)) {
                        send({ type: "SET_SQUARE_COUNTER", counter: value });
                      } else {
                        send({ type: "SET_SQUARE_COUNTER", counter: e.target.value });
                      }
                    }} 
                    className="w-20 h-6" 
                    type="number"
                    min="1"
                  />
                </div>
                <Separator orientation="vertical" className="mx-1 h-6" />
                <div className="flex items-center gap-2">
                  <RadioGroup 
                    value={shapeSize} 
                    onValueChange={(value: "s" | "m" | "l") => send({ type: "SET_SHAPE_SIZE", size: value })}
                    className="flex gap-3"
                  >
                    <div className="flex items-center space-x-1">
                      <RadioGroupItem value="s" id="square-s" className="h-3 w-3" />
                      <Label htmlFor="square-s" className="text-xs">S</Label>
                    </div>
                    <div className="flex items-center space-x-1">
                      <RadioGroupItem value="m" id="square-m" className="h-3 w-3" />
                      <Label htmlFor="square-m" className="text-xs">M</Label>
                    </div>
                    <div className="flex items-center space-x-1">
                      <RadioGroupItem value="l" id="square-l" className="h-3 w-3" />
                      <Label htmlFor="square-l" className="text-xs">L</Label>
                    </div>
                  </RadioGroup>
                </div>
              </Menubar>
            )}

            {circleMode && (
              <Menubar className="absolute rounded-lg h-8 shadow-md justify-center w-fit flex gap-2 bg-sidebar top-10 left-1 z-50 border">
                <div className="flex items-center gap-2">
                  <Input 
                    id="circle-counter"
                    value={circleCounter} 
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value)) {
                        send({ type: "SET_CIRCLE_COUNTER", counter: value });
                      } else {
                        send({ type: "SET_CIRCLE_COUNTER", counter: e.target.value });
                      }
                    }} 
                    className="w-20 h-6" 
                    type="number"
                    min="1"
                  />
                </div>
                <Separator orientation="vertical" className="mx-1 h-6" />
                <div className="flex items-center gap-2">
                  <RadioGroup 
                    value={shapeSize} 
                    onValueChange={(value: "s" | "m" | "l") => send({ type: "SET_SHAPE_SIZE", size: value })}
                    className="flex gap-3"
                  >
                    <div className="flex items-center space-x-1">
                      <RadioGroupItem value="s" id="circle-s" className="h-3 w-3" />
                      <Label htmlFor="circle-s" className="text-xs">S</Label>
                    </div>
                    <div className="flex items-center space-x-1">
                      <RadioGroupItem value="m" id="circle-m" className="h-3 w-3" />
                      <Label htmlFor="circle-m" className="text-xs">M</Label>
                    </div>
                    <div className="flex items-center space-x-1">
                      <RadioGroupItem value="l" id="circle-l" className="h-3 w-3" />
                      <Label htmlFor="circle-l" className="text-xs">L</Label>
                    </div>
                  </RadioGroup>
                </div>
              </Menubar>
            )}
          </div>
          <div className="grid gap-1 justify-center items-center w-full h-[600px]" data-current={current}>
            {canvasApis.map((canvas, index) => (
              <div
                key={index}
                data-current={index === current}
                data-key={index}
                className="h-full w-full items-center justify-center hidden data-[current=true]:flex relative rounded-none bg-[#f9f9fa] z-40"
                ref={(el) => {
                  if (el) {
                    el.innerHTML = '';
                    el.appendChild(canvas.elements.container);
                  }
                }}
              >
              </div>
            ))}
          </div>

          {canvasApis.length > 1 && (
            <ScrollArea className="w-full overflow-x-scroll flex rounded-b-md rounded-t-none py-1 px-1">
              <ScrollBar orientation="horizontal" />
              <div className="flex gap-1 pt-1">
                {canvasApis.map((canvas, index) => (
                  <div key={index} className="relative flex-shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => send({ type: "GO_TO_SLIDE", index })}
                      data-current={index === current}
                      className="size-16 rounded-md overflow-hidden border-2 transition-all duration-200 ease-in-out bg-muted/5 hover:bg-muted/10 hover:scale-105 hover:translate-y-[-2px] p-0 data-[current=true]:border-primary data-[current=true]:ring-3 data-[current=true]:ring-primary/30"
                    >
                      <img 
                        src={canvas.toDataURL()}
                        alt={`Pagina ${index + 1}`}
                        width={64}
                        height={64}
                        style={{ objectFit: 'contain' }}
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        send({ type: "REMOVE_IMAGE", index });
                      }}
                      className="absolute z-50 -top-1 -right-1 bg-destructive hover:bg-destructive/80 text-white hover:text-white p-0 rounded-full border-2 border-background hover:cursor-pointer size-4"
                    >
                      <X className="size-2" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageEditor; 